// frontend/src/components/Projects/ProjectDetails.jsx

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useProjects } from '../../hooks/useProjects'
import { useWebSocketEvents } from '../../hooks/useWebSocketEvents'
import { useWebSocketContext } from '../../context/WebSocketContext'
import { Button } from '../Common/Button'
import { Card } from '../Common/Card'
import { Loading } from '../Common/Loading'
import { ProjectAnalysisWizard } from './ProjectAnalysisWizard'
import { EstimateCategoryTabs } from '../Estimates/EstimateCategoryTabs'
import { SheetGallery } from '../TechnicalCard/SheetGallery'
import { DocumentSearch } from '../TechnicalCard/DocumentSearch'
import { formatDate } from '../../utils/dateFormatter'
import {
  ArrowLeft,
  Download,
  Trash2,
  FileText,
  FileStack,
  DollarSign,
  RussianRuble,
  Calendar,
  AlertCircle,
  CheckCircle,
  Zap,
  Loader,
  Layers,
  ScanSearch,
  Microscope
} from 'lucide-react'
import toast from 'react-hot-toast'

// ============ ПАНЕЛЬ ПРОГРЕССА ФОНОВОЙ ОБРАБОТКИ ============

const TASK_TYPE_CONFIG = {
  processing: {
    label: 'Загрузка листов документа',
    icon: Layers,
    color: 'blue'
  },
  classification: {
    label: 'Разметка листов',
    icon: ScanSearch,
    color: 'purple'
  },
  analysis: {
    label: 'Анализ листов (извлечение данных)',
    icon: Microscope,
    color: 'orange'
  }
}

const ProcessingProgressPanel = ({ progress, projectStatus }) => {
  // Показываем панель если есть активный прогресс,
  // либо проект в статусе processing/analyzing (обработка идёт, но событие ещё не пришло)
  const isProcessingStatus = projectStatus === 'processing' || projectStatus === 'analyzing'

  if (!progress && !isProcessingStatus) return null

  const config = progress
    ? (TASK_TYPE_CONFIG[progress.taskType] || TASK_TYPE_CONFIG.processing)
    : TASK_TYPE_CONFIG.processing

  const Icon = config.icon
  const percent = progress?.percent ?? 0

  const colorMap = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', bar: 'bg-blue-600', text: 'text-blue-700', iconBg: 'bg-blue-100' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', bar: 'bg-purple-600', text: 'text-purple-700', iconBg: 'bg-purple-100' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', bar: 'bg-orange-600', text: 'text-orange-700', iconBg: 'bg-orange-100' }
  }
  const colors = colorMap[config.color]

  // Этапы конвейера для индикации
  const stages = ['processing', 'classification', 'analysis']
  const currentStageIndex = progress ? stages.indexOf(progress.taskType) : 0

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-4`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${colors.iconBg}`}>
          <Icon size={20} className={colors.text} />
        </div>
        <div className="flex-1">
          <p className={`font-semibold ${colors.text}`}>
            {config.label}
          </p>
          <p className="text-sm text-gray-600">
            {progress?.message || 'Обработка документа...'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Loader size={18} className={`animate-spin ${colors.text}`} />
          {progress && (
            <span className={`font-bold ${colors.text}`}>
              {percent}%
            </span>
          )}
        </div>
      </div>

      {/* Прогресс-бар */}
      <div className="w-full bg-white rounded-full h-2.5 overflow-hidden border border-gray-200">
        <div
          className={`h-full ${colors.bar} rounded-full transition-all duration-500`}
          style={{ width: `${progress ? percent : 5}%` }}
        />
      </div>

      {/* Индикатор этапов конвейера */}
      <div className="flex items-center justify-between mt-3">
        {stages.map((stage, idx) => {
          const stageConfig = TASK_TYPE_CONFIG[stage]
          const isDone = idx < currentStageIndex
          const isCurrent = idx === currentStageIndex

          return (
            <div key={stage} className="flex items-center gap-1.5">
              {isDone ? (
                <CheckCircle size={14} className="text-green-600" />
              ) : isCurrent ? (
                <Loader size={14} className={`animate-spin ${colors.text}`} />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300" />
              )}
              <span className={`text-xs ${
                isDone ? 'text-green-700 font-medium'
                : isCurrent ? `${colors.text} font-medium`
                : 'text-gray-400'
              }`}>
                {stageConfig.label.split(' ')[0]}
              </span>
            </div>
          )
        })}
      </div>

      {progress && progress.total > 0 && (
        <p className="text-xs text-gray-500 mt-2 text-right">
          {progress.current} из {progress.total} листов
        </p>
      )}
    </div>
  )
}

export const ProjectDetails = () => {
  const { cardId } = useParams()
  const navigate = useNavigate()
  const { getProjectDetails, deleteProject, downloadReport } = useProjects()
  const { isConnected } = useWebSocketContext()

  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [isDownloading, setIsDownloading] = useState(false)
  // По умолчанию показываем вью просмотра документа.
  // Wizard (поэтапный анализ) открывается только по кнопке "Расчет"
  const [showWizard, setShowWizard] = useState(false)
  const [wizardStep, setWizardStep] = useState('classification')
  // Прогресс фоновой обработки (загрузка листов / классификация / анализ)
  const [processingProgress, setProcessingProgress] = useState(null)

  // Обработчик WebSocket событий
  const handleWebSocketEvents = {
    onTechnicalCardUpdate: (data) => {
      console.log('📨 Обновление карты:', data)

      if (data.card_id === parseInt(cardId)) {
        // Обновляем статус карты
        setProject(prev => prev ? {
          ...prev,
          status: data.status || prev.status
        } : null)

        // При завершении или ошибке скрываем панель прогресса
        if (data.status === 'completed' || data.status === 'error') {
          setProcessingProgress(null)
          if (data.status === 'completed') {
            toast.success('Документ обработан: классификация и анализ завершены')
          }
        }

        // Перезагружаем детали проекта
        loadProjectDetails()
      }
    },

    onSheetUpdate: (data) => {
      console.log('📨 Обновление листа:', data)

      if (data.card_id === parseInt(cardId)) {
        // Перезагружаем листы
        loadProjectDetails()
      }
    },

    onEstimateUpdate: (data) => {
      console.log('📨 Обновление сметы:', data)

      if (data.card_id === parseInt(cardId)) {
        // Перезагружаем сметы
        loadProjectDetails()
      }
    },

    onProgressUpdate: (data) => {
      console.log('📨 Обновление прогресса:', data)

      if (data.card_id === parseInt(cardId)) {
        // Обновляем панель прогресса (загрузка листов / классификация / анализ)
        setProcessingProgress({
          taskType: data.task_type,      // 'processing' | 'classification' | 'analysis'
          current: data.current,
          total: data.total,
          percent: data.progress_percent,
          message: data.message
        })

        // Скрываем панель, когда все этапы завершены
        if (data.task_type === 'analysis' && data.current >= data.total && data.total > 0) {
          setTimeout(() => setProcessingProgress(null), 3000)
        }
      }
    },

    onConnected: (data) => {
      console.log('✅ WebSocket подключен:', data)
    }
  }

  useWebSocketEvents(handleWebSocketEvents)

  useEffect(() => {
    loadProjectDetails()
  }, [cardId])

  const loadProjectDetails = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getProjectDetails(cardId)

      if (!data) {
        throw new Error('Проект не найден')
      }

      const normalizedData = {
        ...data,
        sheets: Array.isArray(data.sheets) ? data.sheets : [],
        estimates: Array.isArray(data.estimates) ? data.estimates : []
      }

      setProject(normalizedData)
      // Не переключаем showWizard автоматически:
      // пользователь всегда попадает на вью просмотра документа,
      // а wizard (поэтапный расчет) открывается только по кнопке "Расчет"
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Ошибка при загрузке деталей проекта'
      setError(errorMessage)
      toast.error(errorMessage)
      console.error('Error loading project details:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить проект? Это действие невозможно отменить.')) {
      return
    }

    setIsDeleting(true)
    try {
      await deleteProject(cardId)
      toast.success('Проект успешно удален')
      navigate('/projects')
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Ошибка при удалении проекта'
      toast.error(errorMessage)
      console.error('Error deleting project:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      await downloadReport(cardId)
      toast.success('Отчет успешно скачан')
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Ошибка при скачивании отчета'
      toast.error(errorMessage)
      console.error('Error downloading report:', error)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleWizardComplete = () => {
    setShowWizard(false)
//     setActiveTab('overview')
    loadProjectDetails()
  }

  const handleToggleWizard = () => {
    setShowWizard(!showWizard)
    setWizardStep('classification')
  }

  if (loading) {
    return <Loading message="Загрузка деталей проекта..." />
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <AlertCircle size={48} className="mx-auto text-red-600 mb-4" />
        <p className="text-gray-600 mb-2">{error || 'Проект не найден'}</p>
        <Button
          variant="primary"
          onClick={() => navigate('/projects')}
          className="mt-4"
        >
          Вернуться к проектам
        </Button>
      </div>
    )
  }

  // Если визард активен, показать его
  if (showWizard) {
    return (
      <div className="space-y-6">
          {/* Индикатор подключения WebSocket */}
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
            <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-600' : 'bg-red-600'}`}></div>
            <span className="text-sm text-gray-700">
              {isConnected ? 'Подключено к серверу' : 'Отключено от сервера'}
            </span>
          </div>
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Назад к проектам
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleToggleWizard}
            className="flex items-center gap-2"
          >
            Просмотр проекта
          </Button>
        </div>

        <ProjectAnalysisWizard
          project={project}
          initialStep={wizardStep}
          onStepChange={setWizardStep}
          onComplete={handleWizardComplete}
        />
      </div>
    )
  }

  // Обычный вид для завершенных проектов
  const getStatusBadge = (status) => {
    const statusConfig = {
      'completed': {
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Завершено',
        icon: CheckCircle
      },
      'processing': {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        label: 'Обработка',
        icon: null
      },
      'error': {
        bg: 'bg-red-100',
        text: 'text-red-800',
        label: 'Ошибка',
        icon: AlertCircle
      },
      'pending': {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        label: 'Ожидание',
        icon: null
      }
    }

    const config = statusConfig[status] || statusConfig['pending']
    const IconComponent = config.icon

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {IconComponent && <IconComponent size={16} />}
        {config.label}
      </span>
    )
  }

  const tabs = [
    {
      id: 'overview',
      label: 'Обзор'
    },
    {
      id: 'sheets',
      label: `Листы (${project.sheets?.length || 0})`
    },
    {
      id: 'search',
      label: 'Поиск по документу'
    },
    {
      id: 'estimates',
      label: `Сметы (${project.estimates?.length || 0})`
    }
  ]

  const sheetStats = {
    total: project.sheets?.length || 0,
    materials: project.sheets?.filter(s => s.sheet_type === 'materials').length || 0,
    assembly: project.sheets?.filter(s => s.sheet_type === 'assembly').length || 0,
    operational: project.sheets?.filter(s => s.sheet_type === 'operational').length || 0
  }

//   const [activeTab, setActiveTab] = useState('overview')

  return (
    <div className="space-y-6">
        {/* Индикатор подключения WebSocket */}
      <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-600' : 'bg-red-600'}`}></div>
        <span className="text-sm text-gray-700">
          {isConnected ? 'Подключено к серверу' : 'Отключено от сервера'}
        </span>
      </div>

      {/* Панель прогресса фоновой обработки документа */}
      <ProcessingProgressPanel progress={processingProgress} projectStatus={project.status} />

      {/* Заголовок и навигация */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/projects')}
          className="flex items-center gap-2"
        >
          <ArrowLeft size={20} />
          Назад
        </Button>
{/*          {project.status !== 'completed' && ( */}
{/*           <Button */}
{/*             variant="primary" */}
{/*             size="sm" */}
{/*             onClick={handleToggleWizard} */}
{/*             className="flex items-center gap-2" */}
{/*           > */}
{/*             <Zap size={18} /> */}
{/*             Анализ */}
{/*           </Button> */}
{/*          )} */}
      </div>

      {/* Основная информация */}
      <Card className="border-l-4 border-l-blue-600">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText size={32} className="text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{project.file_name}</h1>
                <div className="mt-2">
                  {getStatusBadge(project.status)}
                </div>
              </div>
            </div>

            {/* Информация о проекте - сетка */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <FileStack size={20} className="text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Всего страниц</p>
                  <p className="text-lg font-semibold text-gray-900">{project.total_pages}</p>
                </div>
              </div>

{/*               <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"> */}
{/*                 <Calendar size={20} className="text-gray-400" /> */}
{/*                 <div> */}
{/*                   <p className="text-sm text-gray-600">Дата создания</p> */}
{/*                   <p className="text-lg font-semibold text-gray-900"> */}
{/*                     {formatDate(project.created_at)} */}
{/*                   </p> */}
{/*                 </div> */}
{/*               </div> */}

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <RussianRuble size={20} className="text-green-400" />
                <div>
                  <p className="text-sm text-gray-600">Общая стоимость по сметам</p>
                  <p className="text-lg font-semibold text-green-600">
                    {project.total_cost?.toLocaleString('ru-RU', {
                      style: 'currency',
                      currency: 'RUB',
                      minimumFractionDigits: 0
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <FileStack size={20} className="text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Сметы</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {project.estimates?.length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Кнопки действий */}
          <div className="flex flex-col gap-2 md:w-48">
{/*                {project.status !== 'completed' && ( */}
          <Button
            variant="primary"
            onClick={handleToggleWizard}
            className="flex items-center gap-2"
          >
            <Zap size={18} />
            Расчет
          </Button>
{/*          )} */}
            <Button
              variant="primary"
              onClick={handleDownload}
              disabled={project.status !== 'completed' || isDownloading}
              className="flex items-center justify-center gap-2 w-full"
              title={project.status !== 'completed' ? 'Отчет доступен только для завершенных проектов' : ''}
            >
              <Download size={18} />
              <span>{isDownloading ? 'Скачивание...' : 'Скачать отчет'}</span>
            </Button>
{/*             <Button */}
{/*               variant="danger" */}
{/*               onClick={handleDelete} */}
{/*               disabled={project.status !== 'completed' || isDownloading} */}
{/*               className="flex items-center justify-center gap-2 w-full" */}
{/*             > */}
{/*               <Trash2 size={18} /> */}
{/*               <span>{isDeleting ? 'Удаление...' : 'Удалить'}</span> */}
{/*             </Button> */}
          </div>
        </div>
      </Card>

      {/* Табы */}
      <div className="border-b border-gray-200 bg-white rounded-t-lg">
        <div className="flex gap-8 px-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-4 font-medium border-b-2 transition-all duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Содержимое табов */}
      <div className="bg-white rounded-b-lg p-6">
        {/* Обзор */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Статистика листов */}
            <Card>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Статистика листов</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <span className="text-gray-600">Всего листов</span>
                  <span className="text-2xl font-bold text-gray-900">{sheetStats.total}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-600">Материальные листы</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{sheetStats.materials}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-600">Сборочные листы</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{sheetStats.assembly}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span className="text-gray-600">Операционные листы</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{sheetStats.operational}</span>
                </div>
              </div>
            </Card>

            {/* Сметы */}
            <Card>
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Сметы</h2>
              <div className="space-y-3">
                {project.estimates && project.estimates.length > 0 ? (
                  project.estimates.map((estimate) => (
                    <div
                      key={estimate.id}
                      className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div>
                        <p className="font-medium text-gray-900 capitalize">
                          {getEstimateTypeLabel(estimate.estimate_type)}
                        </p>
                        <p className="text-sm text-gray-600">
                          {estimate.data?.items?.length || 0} позиций
                        </p>
                      </div>
                      <p className="text-lg font-semibold text-green-600">
                        {estimate.total_cost?.toLocaleString('ru-RU', {
                          style: 'currency',
                          currency: 'RUB',
                          minimumFractionDigits: 0
                        })}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Сметы отсутствуют</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Листы */}
        {activeTab === 'sheets' && (
          <div>
            {project.sheets && project.sheets.length > 0 ? (
              <SheetGallery sheets={project.sheets} />
            ) : (
              <div className="text-center py-12">
                <FileStack size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Листы отсутствуют</p>
              </div>
            )}
          </div>
        )}

        {/* Поиск по документу */}
        {activeTab === 'search' && (
          <div>
            <DocumentSearch
              cardId={project.id}
              onOpenSheet={(sheetId, pageNumber) => {
                // Переходим на вкладку "Листы" - там открывается галерея
                setActiveTab('sheets')
                toast(`Лист ${pageNumber} — используйте навигацию галереи`, { icon: '📄' })
              }}
            />
          </div>
        )}

        {/* Сметы: подвкладки Работы / Оборудование / Материалы */}
        {activeTab === 'estimates' && (
          <div>
            <EstimateCategoryTabs
              estimates={project.estimates || []}
              sheets={project.sheets || []}
              cardId={project.id}
              onRefresh={loadProjectDetails}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function getEstimateTypeLabel(type) {
  const labels = {
    'materials': 'Смета материалов',
    'equipment': 'Смета оборудования',
    'operations': 'Смета операций',
    'gesn': 'ЛСР (ГЭСН)'
  }
  return labels[type] || type
}

export default ProjectDetails
